import moment from 'moment'
import { API, graphqlOperation } from 'aws-amplify'
import { ofType } from 'redux-observable'
import { defer, empty, from, of } from 'rxjs'
import { catchError, map, mergeMap, pluck, switchMap, withLatestFrom } from 'rxjs/operators'
import Actions from '../actions'
import i18n from 'resources/locales/i18n'
import { updateGrinUser } from 'graphql/mutations'
import { logError } from 'utils/logUtils'
import { trackEvent } from 'utils/analyticsUtils'
import {
  searchPatientSearchModelsForPmsCreateOrAdd,
  searchPatientSearchModelsForPmsRedirect
} from 'graphql/customQueries'
import { SEARCH_RESULT_LIMIT } from 'consts/patientsConsts'
import { buildPatientScopeFilter, buildPatientSearchFilter, MAX_SEARCH_LEN } from 'utils/searchUtils'
import { classifyPmsPatientMatches, PmsMatchOutcomes, PMS_BIRTH_DATE_FORMATS } from 'utils/pmsPatientMatchUtils'
import { scrubGraphqlError } from 'utils/pmsErrorUtils'
import { ROUTES } from 'consts'
import { getAccessToken } from 'utils/storageUtils'

export const pmsAuthenticationEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTHENTICATION),
    pluck('payload'),
    mergeMap(({ pmsType, pmsToken, onBehalfOf }) => {
      if (pmsToken) {
        return of(Actions.pmsAuthenticationReceived({ access_token: pmsToken }))
      }

      const queryString = onBehalfOf ? `?onBehalfOf=${encodeURIComponent(onBehalfOf)}` : ''

      return from(API.get('grinServerlessApi', `/pms/v1/${pmsType}/auth${queryString}`)).pipe(
        mergeMap(data => of(Actions.pmsAuthenticationReceived(data))),
        catchError(err => of(Actions.pmsAuthenticationFailed(err)))
      )
    })
  )

export const pmsAuthenticationReceivedEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTHENTICATION_RECEIVED),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) => {
      localStorage.setItem(`${pmsReducer.pmsType}_token`, payload.access_token)

      return empty()
    })
  )

export const pmsFetchPatientsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_PATIENTS),
    withLatestFrom(state$),
    switchMap(([{ payload: searchValue }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients?searchInput=${searchValue}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ patients }) => of(Actions.pmsFetchPatientsReceived({ patients }))),
        catchError(err => of(Actions.pmsFetchPatientsFailed(err)))
      )
    )
  )

export const pmsRequestFailedEpic = action$ =>
  action$.pipe(
    ofType(
      Actions.PMS_FETCH_PATIENTS_FAILED,
      Actions.PMS_LINK_PATIENT_FAILED,
      Actions.PMS_UNLINK_PATIENT_FAILED,
      Actions.PMS_FETCH_PATIENT_FAILED,
      Actions.PMS_FETCH_APPOINTMENTS_FAILED,
      Actions.PMS_FETCH_AVAILABLE_TIME_SLOTS_FAILED,
      Actions.PMS_CREATE_NEW_APPOINTMENT_FAILED,
      Actions.PMS_DELETE_APPOINTMENT_FAILED,
      Actions.PMS_FETCH_STATIC_DATA_FAILED,
      Actions.PMS_AUTHENTICATION_FAILED
    ),
    map(({ payload: error }) => {
      logError('An error occured while integrating with pms', error)

      const errorData = error?.response?.data

      return Actions.showSnackbar({
        type: 'warning',
        text:
          errorData?.code === 'PMS_DOWN'
            ? i18n.t('dialogs.patientInfo.appointments.pms.dolphinDownError')
            : errorData?.message?.toLowerCase()?.includes('unknown error')
            ? i18n.t('dialogs.patientInfo.appointments.pms.unknownPmsError')
            : i18n.t('messages.somethingWentWrongContactSupport')
      })
    })
  )

export const pmsLinkPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_LINK_PATIENT),
    withLatestFrom(state$),
    map(([{ payload }, { patientsReducer, pmsReducer }]) => {
      const patientGrinUser = patientsReducer.patientCard.patient.user

      return {
        patientGrinUser,
        patientExternalIds: JSON.parse(patientGrinUser.externalIds || '{}'),
        pmsPatientId: payload.pmsPatientId,
        pmsType: payload.pmsType,
        isSilent: !!payload?.isSilent
      }
    }),
    switchMap(({ patientGrinUser, pmsPatientId, pmsType, patientExternalIds, isSilent }) =>
      from(
        API.graphql(
          graphqlOperation(updateGrinUser, {
            input: {
              id: patientGrinUser.id,
              _version: patientGrinUser._version,
              externalIds: JSON.stringify({ ...patientExternalIds, [pmsType]: pmsPatientId })
            }
          })
        )
      ).pipe(
        mergeMap(response => of(Actions.pmsLinkPatientReceived({ user: response.data.updateGrinUser, isSilent }))),
        catchError(err => of(Actions.pmsLinkPatientFailed(err)))
      )
    )
  )

export const pmsLinkPatientReceviedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_LINK_PATIENT_RECEIVED),
    switchMap(({ payload }) => {
      if (!payload.isSilent) {
        return of(
          Actions.showSnackbar({
            type: 'success',
            text: i18n.t('pms.linkedPatientSuccessMessage')
          })
        )
      }

      return empty()
    })
  )

export const pmsUnlinkPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_UNLINK_PATIENT),
    withLatestFrom(state$),
    map(([{ payload }, { patientsReducer }]) => {
      const patientGrinUser = patientsReducer.patientCard.patient.user
      let patientExternalIds = JSON.parse(patientGrinUser.externalIds || '{}')

      delete patientExternalIds[payload.pmsType]

      return {
        patientGrinUser,
        patientExternalIds: JSON.stringify(patientExternalIds),
        pmsType: payload.pmsType
      }
    }),
    switchMap(({ patientGrinUser, pmsType, patientExternalIds }) =>
      from(
        API.graphql(
          graphqlOperation(updateGrinUser, {
            input: {
              id: patientGrinUser.id,
              _version: patientGrinUser._version,
              externalIds: patientExternalIds
            }
          })
        )
      ).pipe(
        mergeMap(response => of(Actions.pmsUnlinkPatientReceived(response.data.updateGrinUser))),
        catchError(err => of(Actions.pmsUnlinkPatientFailed(err)))
      )
    )
  )

export const pmsFetchAppointmentsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_APPOINTMENTS),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments?patientId=${payload}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ appointments }) => of(Actions.pmsFetchAppointmentsReceived({ appointments }))),
        catchError(err => of(Actions.pmsFetchAppointmentsFailed(err)))
      )
    )
  )

export const pmsFetchPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_PATIENT),
    withLatestFrom(state$),
    switchMap(([{ payload: patientId }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients/${patientId}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ patient, appointments }) => of(Actions.pmsFetchPatientReceived({ patient, appointments }))),
        catchError(err => of(Actions.pmsFetchPatientFailed(err)))
      )
    )
  )

export const pmsFetchAvailableTimeSlotsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_AVAILABLE_TIME_SLOTS),
    withLatestFrom(state$),
    switchMap(([{ payload: queryParams }, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments/timeslots?${queryParams}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ availableTimeSlots }) => of(Actions.pmsFetchAvailableTimeSlotsReceived({ availableTimeSlots }))),
        catchError(err => of(Actions.pmsFetchAvailableTimeSlotsFailed(err)))
      )
    )
  )

export const pmsCreateNewAppointmentEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_NEW_APPOINTMENT),
    withLatestFrom(state$),
    switchMap(([{ payload: appointmentData }, { pmsReducer }]) =>
      from(
        API.post('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments`, {
          body: {
            appointmentData
          },
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(({ appointment }) => of(Actions.pmsCreateNewAppointmentReceived({ appointment }))),
        catchError(err => of(Actions.pmsCreateNewAppointmentFailed(err)))
      )
    )
  )

export const pmsCreateNewAppointmentReceivedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_NEW_APPOINTMENT_RECEIVED),
    map(() =>
      Actions.showSnackbar({
        type: 'success',
        text: i18n.t('pms.newAppointmentSuccessMessage')
      })
    )
  )

export const pmsDeleteAppointmentEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_DELETE_APPOINTMENT),
    withLatestFrom(state$),
    switchMap(([{ payload: appointmentId }, { pmsReducer }]) =>
      from(
        API.del('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/appointments/${appointmentId}`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(() => of(Actions.pmsDeleteAppointmentReceived())),
        catchError(err => of(Actions.pmsDeleteAppointmentFailed(err)))
      )
    )
  )

export const pmsDeleteAppointmentReceivedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_DELETE_APPOINTMENT_RECEIVED),
    map(() =>
      Actions.showSnackbar({
        type: 'success',
        text: i18n.t('pms.deleteAppointmentSuccessMessage')
      })
    )
  )

export const pmsFetchStaticDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_STATIC_DATA),
    withLatestFrom(state$),
    switchMap(([, { pmsReducer }]) =>
      from(
        API.get('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/staticData`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          }
        })
      ).pipe(
        mergeMap(staticData => of(Actions.pmsFetchStaticDataReceived(staticData))),
        catchError(err => of(Actions.pmsFetchStaticDataFailed(err)))
      )
    )
  )

export const pmsAutomaticLinkingAttemptEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_AUTOMATIC_LINKING_ATTEMPT),
    withLatestFrom(state$),
    switchMap(([{ payload }, { pmsReducer }]) =>
      from(
        API.post('grinServerlessApi', `/pms/v1/${pmsReducer.pmsType}/patients/autolink`, {
          headers: {
            'x-api-key': getAccessToken(),
            'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
          },
          body: {
            patientDetails: payload
          }
        })
      ).pipe(
        mergeMap(response => {
          if (response?.matchingPatient) {
            trackEvent('Dolphin linkage - auto patient linking succeeded', {
              matchingParameter: response?.matchingParameter
            })
            return of(
              Actions.pmsLinkPatient({
                pmsPatientId: response.matchingPatient.id,
                pmsType: pmsReducer.pmsType,
                isSilent: true
              })
            )
          }

          trackEvent('Dolphin linkage - patient auto linking failed - no match was found')
          return empty()
        }),
        catchError(err => of(Actions.pmsAutomaticLinkingAttemptFailed(err)))
      )
    )
  )

export const pmsFetchLatestPatientsEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_LATEST_PATIENTS),
    withLatestFrom(state$),
    switchMap(([, { pmsReducer }]) =>
      from(
        API.get(
          'grinServerlessApi',
          `/pms/v1/${pmsReducer.pmsType}/patients/latest?latestTimestamp=${moment()
            .subtract('months', 2)
            .toISOString()}`,
          {
            headers: {
              'x-api-key': getAccessToken(),
              'pms-token': localStorage.getItem(`${pmsReducer.pmsType}_token`)
            }
          }
        )
      ).pipe(
        mergeMap(patientsToInvite => of(Actions.pmsFetchLatestPatientsReceived(patientsToInvite))),
        catchError(err => of(Actions.pmsFetchLatestPatientsFailed(err)))
      )
    )
  )

export const pmsFetchLatestPatientsFailedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_FETCH_LATEST_PATIENTS_FAILED),
    switchMap(({ payload: error }) => {
      logError('An error occured while trying to fetch latest patients for doctor', error)
      return empty()
    })
  )

export const pmsRedirectFailedEpic = action$ =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_FAILED),
    map(({ payload: error }) => {
      logError('An error occured while redirecting to Grin from Dolphin', { error })

      return Actions.showSnackbar({
        type: 'error',
        text: i18n.t('pms.redirectPatientNotFoundModal.error')
      })
    })
  )

/**
 * Wrap a patient-search filter in an AND with the caller's own scope.
 *
 * `buildPatientSearchFilter` returns a bare `or` and only spreads the scope into two of its four
 * branches, so an exact `id` or `username` hit is unscoped. The API is transformer v1, where
 * `@auth` rules are not applied to `@searchable` result filtering, so nothing backstops that
 * server-side. Applied here rather than in the shared helper because other callers pass a term the
 * user typed, while this one takes it from a URL an outside vendor controls.
 */
const scopeFilterToDoctor = (filter, doctorUsername, allowedGroups) => {
  const scope = buildPatientScopeFilter(doctorUsername, allowedGroups)

  return scope ? { and: [filter, scope] } : filter
}

/**
 * Combined `action=createoradd` PMS quick link: one URL that opens an existing patient when it can
 * identify exactly one, and otherwise falls through to creating or disambiguating.
 *
 * Candidates come from the same filter the rest of the app searches patients with, so "similar
 * name" means the same thing here as it does in the patients list.
 */
export const pmsCreateOrAddSearchPatientEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_OR_ADD_SEARCH_PATIENT),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { doctor } = state.profileReducer
      // Bound the term before it reaches OpenSearch. buildPatientSearchFilter does no bounding of
      // its own, and this one comes straight from a URL an outside vendor controls.
      const searchTerm = [payload.firstName, payload.lastName].filter(Boolean).join(' ').trim().slice(0, MAX_SEARCH_LEN)

      // Defence in depth for the scoping above: an unscoped filter would search across practices.
      if (!doctor?.username) {
        history.push(ROUTES.PATIENTS)

        return of(Actions.pmsRedirectFailed({ message: 'createoradd dispatched before the doctor scope was ready' }))
      }

      if (!searchTerm) {
        // Navigate before reporting: Landing renders a permanent loader, so failing in place
        // would strand the user on a spinner behind the error snackbar.
        history.push(ROUTES.PATIENTS)

        return of(
          Actions.pmsRedirectFailed({
            message: 'Missing patient name for the createoradd action'
          })
        )
      }

      // Parse before building the request. A throw inside the graphqlOperation arguments happens
      // before the observable exists, so the catchError below could not see it — it would escape
      // switchMap and kill this epic for the rest of the session.
      let allowedGroups
      try {
        allowedGroups = JSON.parse(doctor?.user?.allowed_groups_permissions || '[]')
      } catch (err) {
        history.push(ROUTES.PATIENTS)

        return of(
          Actions.pmsRedirectFailed({
            message: 'Malformed allowed_groups_permissions for the doctor',
            name: err?.name
          })
        )
      }

      return from(
        API.graphql(
          graphqlOperation(searchPatientSearchModelsForPmsCreateOrAdd, {
            limit: SEARCH_RESULT_LIMIT,
            // AND the doctor scope over the whole filter. buildPatientSearchFilter spreads the
            // scope into its name and email branches only — its `id` and `username` branches are
            // unscoped, and this is the first caller fed a vendor-controlled URL parameter, so a
            // supplied uuid could otherwise surface a patient from another practice.
            filter: scopeFilterToDoctor(
              buildPatientSearchFilter(searchTerm, doctor?.username, allowedGroups),
              doctor?.username,
              allowedGroups
            ),
            // Deterministic ordering so which candidates survive the limit is stable.
            sort: {
              field: 'lastMessageTimestamp',
              direction: 'desc'
            }
          })
        )
      ).pipe(
        mergeMap(response => {
          const results = response.data.searchPatientSearchModels

          return of(
            Actions.pmsCreateOrAddSearchPatientReceived({
              ...payload,
              candidates: results?.items || [],
              // A second exact match could sit outside the page, so uniqueness is unprovable.
              // Fails closed: an absent `total` means the server did not tell us how many matches
              // exist, and a full page is indistinguishable from a truncated one.
              resultsTruncated:
                results?.total == null ||
                results.total > SEARCH_RESULT_LIMIT ||
                (results?.items || []).length >= SEARCH_RESULT_LIMIT
            })
          )
        }),
        catchError(err => {
          // Landing renders a permanent loader, so navigate before reporting or the doctor is
          // stranded on a spinner behind the snackbar.
          history.push(ROUTES.PATIENTS)

          return of(Actions.pmsRedirectFailed(scrubGraphqlError(err)))
        })
      )
    })
  )

export const pmsCreateOrAddSearchPatientReceivedEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_CREATE_OR_ADD_SEARCH_PATIENT_RECEIVED),
    pluck('payload'),
    // defer + a per-action catchError, deliberately: a catchError on the outer pipe would emit its
    // fallback and then COMPLETE the stream, leaving the epic deaf to every later action for the
    // rest of the session. Errors have to be contained to the action that caused them.
    mergeMap(payload =>
      // Destructure inside the defer, not in the parameter list: a throw during destructuring
      // would happen before defer runs and would escape the containment below.
      defer(() => {
        const { firstName, lastName, birthDate, patientId, source, canCreatePatient, candidates, resultsTruncated } =
          payload || {}
        const candidateList = candidates || []
        const { outcome, matchedPatient, suggestions, exactMatches } = classifyPmsPatientMatches({
          firstName,
          lastName,
          birthDate,
          candidates: candidateList,
          resultsTruncated
        })

        const patientName = [firstName, lastName].filter(Boolean).join(' ').trim()
        const parsedBirthDate = moment(birthDate, PMS_BIRTH_DATE_FORMATS, true)
        const createInitialValues = {
          firstName: firstName || '',
          lastName: lastName || '',
          birthDate: parsedBirthDate.isValid() ? parsedBirthDate.toDate() : null,
          note: patientId || ''
        }

        trackEvent('PMS Create or Add - resolved', {
          outcome,
          candidateCount: candidateList.length,
          resultsTruncated: !!resultsTruncated,
          canCreatePatient,
          source
        })

        if (outcome === PmsMatchOutcomes.Open) {
          history.push(`${ROUTES.PATIENTS}/${matchedPatient.id}`)
          return empty()
        }

        history.push(ROUTES.PATIENTS)

        if (outcome === PmsMatchOutcomes.Create) {
          // Without the plan flag the create modal is not mounted at all, so fall back to the
          // existing not-found modal rather than dispatching into the void.
          return canCreatePatient
            ? of(Actions.setCreateRecordsPatientModalOpen({ isOpen: true, initialValues: createInitialValues }))
            : of(Actions.pmsToggleRedirectPatientNotFoundModal({ isOpen: true, pmsPatientName: patientName }))
        }

        return of(
          Actions.pmsToggleCreateOrAddModal({
            isOpen: true,
            patientName,
            suggestions,
            // Two or more equally exact matches land here too, and need different wording from
            // "we couldn't find an exact match".
            exactMatchCount: exactMatches.length,
            createInitialValues: canCreatePatient ? createInitialValues : null
          })
        )
      }).pipe(
        catchError(err => {
          history.push(ROUTES.PATIENTS)

          return of(Actions.pmsRedirectFailed(scrubGraphqlError(err)))
        })
      )
    )
  )

export const pmsRedirectSearchLinkedPatientEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_SEARCH_LINKED_PATIENT),
    pluck('payload'),
    switchMap(({ pmsPatientId, pmsPatientName }) =>
      from(
        API.graphql(
          graphqlOperation(searchPatientSearchModelsForPmsRedirect, {
            filter: {
              or: [
                {
                  externalIdsList: {
                    match: pmsPatientId
                  }
                },
                {
                  name: {
                    matchPhrasePrefix: pmsPatientName
                  }
                }
              ]
            }
          })
        )
      ).pipe(
        mergeMap(response =>
          of(
            Actions.pmsRedirectSearchLinkedPatientReceived({
              patientsResults: response.data.searchPatientSearchModels?.items,
              pmsPatientName
            })
          )
        ),
        catchError(err => of(Actions.pmsRedirectFailed(err)))
      )
    )
  )

export const pmsRedirectSearchLinkedPatientReceivedEpic = (action$, state$, { history }) =>
  action$.pipe(
    ofType(Actions.PMS_REDIRECT_SEARCH_LINKED_PATIENT_RECEIVED),
    pluck('payload'),
    mergeMap(({ patientsResults, pmsPatientName }) => {
      switch (patientsResults.length) {
        case 0:
          history.push(ROUTES.PATIENTS)
          return of(Actions.pmsToggleRedirectPatientNotFoundModal({ isOpen: true, pmsPatientName }))
        case 1:
          history.push(`${ROUTES.PATIENTS}/${patientsResults[0].id}`)
          return empty()
        default:
          history.push(`${ROUTES.PATIENTS}`)
          return of(Actions.setPatientsPresetFilter({ name: pmsPatientName }))
      }
    }),
    catchError(err => of(Actions.pmsRedirectFailed(err)))
  )
